import { paymixVariationFraction } from "@shared/constants/paymix";
import type { InvestmentRound } from "@shared/types/investment-rounds";
import { useEffect, useMemo, useState } from "react";
import { formatCurrency, formatSharesRange } from "@/lib/constants";

export type { InvestmentRound };

export type PaymixCalculatorProps = {
  baseSalary: number;
  shares: number;
  fxRate: number;
  signingBonus?: number;
  performanceBonus?: number;
  performanceBonusShares?: number;
  performanceBonusSharesMax?: number;
  allowSlider: boolean;
  /** PayMix variation as a percentage (e.g. 10 = ±10%). Defaults to the standard. */
  variationPct?: number;
  rounds: InvestmentRound[];
  strikePrice: number;
  onValuesChange: (values: { salary: number; shares: number }) => void;
};

const STEP = 100;

function roundToStep(value: number, step: number): number {
  return Math.round(value / step) * step;
}

export function PaymixCalculator({
  baseSalary,
  shares,
  fxRate,
  signingBonus = 0,
  performanceBonus = 0,
  performanceBonusShares = 0,
  performanceBonusSharesMax,
  allowSlider,
  variationPct,
  rounds,
  strikePrice,
  onValuesChange,
}: PaymixCalculatorProps) {
  const SALARY_VARIATION = paymixVariationFraction(variationPct);
  const currentRound = useMemo(
    () => rounds.find((r) => r.section === "current") ?? rounds[0],
    [rounds],
  );
  const spread = currentRound ? currentRound.pricePerShare - strikePrice : 0;
  const hasEquity = shares > 0 && spread > 0;

  const desiredRange = roundToStep(baseSalary * SALARY_VARIATION, STEP);
  const minSalary = baseSalary - desiredRange;
  const maxSalary = baseSalary + desiredRange;

  const midAnnualSalary = baseSalary * 12;
  const midEquityAnnualBRL = hasEquity ? ((shares * spread) / 4) * fxRate : 0;
  const cashEquityTotal = midAnnualSalary + midEquityAnnualBRL;

  const maxShares = hasEquity
    ? Math.round(((cashEquityTotal - minSalary * 12) * 4) / (spread * fxRate))
    : 0;
  const minShares = hasEquity
    ? Math.max(0, Math.round(((cashEquityTotal - maxSalary * 12) * 4) / (spread * fxRate)))
    : 0;

  const [currentSalary, setCurrentSalary] = useState(baseSalary);

  useEffect(() => {
    setCurrentSalary(baseSalary);
  }, [baseSalary]);

  const currentShares = useMemo(() => {
    if (!hasEquity) return 0;
    if (maxSalary === minSalary) return shares;
    const interpolated =
      maxShares - ((currentSalary - minSalary) / (maxSalary - minSalary)) * (maxShares - minShares);
    return Math.round(interpolated);
  }, [currentSalary, hasEquity, maxShares, minShares, minSalary, maxSalary, shares]);

  useEffect(() => {
    onValuesChange({ salary: currentSalary, shares: currentShares });
  }, [currentSalary, currentShares, onValuesChange]);

  const annualSalary = currentSalary * 12;
  const equityAnnualBRL = hasEquity ? ((currentShares * spread) / 4) * fxRate : 0;
  const perfBonusSharesMax = performanceBonusSharesMax || performanceBonusShares;
  const perfBonusSharesBRLMax =
    perfBonusSharesMax > 0 && spread > 0 ? ((perfBonusSharesMax * spread) / 4) * fxRate : 0;
  const perfBonusSharesBRLMin =
    performanceBonusShares > 0 && spread > 0 ? ((performanceBonusShares * spread) / 4) * fxRate : 0;
  const totalComp =
    annualSalary +
    equityAnnualBRL +
    (signingBonus || 0) +
    (performanceBonus || 0) +
    perfBonusSharesBRLMax;

  const compCards: {
    label: string;
    value: string;
    sublabel?: string;
    highlight?: boolean;
    positive?: boolean;
  }[] = [
    { label: "Salário Anual", value: formatCurrency(annualSalary) },
    ...(hasEquity
      ? [
          {
            label: "Equity Anual",
            value: formatCurrency(equityAnnualBRL),
            sublabel: `${currentShares.toLocaleString("pt-BR")} ações`,
            highlight: true,
          },
        ]
      : []),
    ...(perfBonusSharesBRLMax > 0
      ? [
          {
            label: "Equity Bonus",
            value:
              perfBonusSharesBRLMin !== perfBonusSharesBRLMax
                ? `${formatCurrency(perfBonusSharesBRLMin)} – ${formatCurrency(perfBonusSharesBRLMax)}`
                : formatCurrency(perfBonusSharesBRLMax),
            sublabel: formatSharesRange(performanceBonusShares, performanceBonusSharesMax),
          },
        ]
      : []),
    ...((signingBonus ?? 0) > 0
      ? [{ label: "Signing Bonus", value: formatCurrency(signingBonus) }]
      : []),
    ...((performanceBonus ?? 0) > 0
      ? [{ label: "Perf. Bonus", value: formatCurrency(performanceBonus) }]
      : []),
    { label: "Total Comp", value: formatCurrency(totalComp), positive: true },
  ];
  const gridColsClass =
    compCards.length <= 2
      ? "grid-cols-2"
      : compCards.length === 3
        ? "grid-cols-2 sm:grid-cols-3"
        : compCards.length === 4
          ? "grid-cols-2 sm:grid-cols-4"
          : compCards.length === 5
            ? "grid-cols-2 sm:grid-cols-5"
            : "grid-cols-2 sm:grid-cols-6";

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentSalary(Number(e.target.value));
  };

  const formatCompact = (value: number): string => {
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(0)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(0)}M`;
    return `$${value.toLocaleString("en-US")}`;
  };

  return (
    <div className="space-y-6">
      {allowSlider && hasEquity && (
        <div className="rounded-lg border bg-muted/30 p-4">
          <label
            htmlFor="paymix-slider"
            className="text-xs font-semibold uppercase text-muted-foreground"
          >
            Ajuste de PayMix
          </label>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-xs text-pink-600 whitespace-nowrap">+ Equity</span>
            <input
              id="paymix-slider"
              type="range"
              min={minSalary}
              max={maxSalary}
              step={STEP}
              value={currentSalary}
              onChange={handleSliderChange}
              className="w-full accent-pink-600"
            />
            <span className="text-xs text-blue-600 whitespace-nowrap">+ Cash</span>
          </div>
          <div className="mt-1 flex justify-between text-xs text-muted-foreground">
            <span>{formatCurrency(minSalary)}</span>
            <span className="font-semibold text-foreground">
              {formatCurrency(currentSalary)}/mês
            </span>
            <span>{formatCurrency(maxSalary)}</span>
          </div>
        </div>
      )}

      <div className={`grid gap-3 ${gridColsClass}`}>
        {compCards.map((card) => (
          <CompCard key={card.label} {...card} />
        ))}
      </div>

      {((signingBonus ?? 0) > 0 || perfBonusSharesBRLMax > 0 || (performanceBonus ?? 0) > 0) && (
        <div className="space-y-2">
          {(signingBonus ?? 0) > 0 && (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-2.5 text-xs">
              <span className="font-semibold uppercase tracking-wide text-muted-foreground">
                Bônus de Assinatura:
              </span>
              <span className="font-bold text-sm">{formatCurrency(signingBonus)}</span>
            </div>
          )}
          {perfBonusSharesBRLMax > 0 && (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-2.5 text-xs">
              <span className="font-semibold uppercase tracking-wide text-muted-foreground">
                Equity Bonus:
              </span>
              <span className="font-bold text-sm">
                {perfBonusSharesBRLMin !== perfBonusSharesBRLMax
                  ? `${formatCurrency(perfBonusSharesBRLMin)} – ${formatCurrency(perfBonusSharesBRLMax)}`
                  : formatCurrency(perfBonusSharesBRLMax)}
              </span>
            </div>
          )}
          {(performanceBonus ?? 0) > 0 && (
            <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-2.5 text-xs">
              <span className="font-semibold uppercase tracking-wide text-muted-foreground">
                Bônus de Performance:
              </span>
              <span className="font-bold text-sm">{formatCurrency(performanceBonus)}</span>
            </div>
          )}
        </div>
      )}

      {hasEquity && (
        <div>
          <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
            Projeção de Equity por Rodada
          </h4>
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium">Rodada</th>
                  <th className="text-right px-3 py-2 font-medium">Valuation</th>
                  <th className="text-right px-3 py-2 font-medium">Preço/Ação</th>
                  <th className="text-right px-3 py-2 font-medium">Ganho Potencial</th>
                </tr>
              </thead>
              <tbody>
                {rounds.map((round) => {
                  const profit = currentShares * round.pricePerShare - currentShares * strikePrice;
                  const profitBRL = profit * fxRate;
                  const isPast = round.section === "past";
                  const isCurrent = round.section === "current";

                  return (
                    <tr
                      key={round.name}
                      className={`border-b last:border-0 ${isCurrent ? "bg-amber-50 dark:bg-amber-950/20" : ""} ${isPast ? "text-muted-foreground" : ""}`}
                    >
                      <td className="px-3 py-2">
                        {round.name}
                        {isCurrent && (
                          <span className="ml-2 inline-block rounded-full bg-amber-500 px-2 py-0.5 text-[10px] text-white">
                            atual
                          </span>
                        )}
                      </td>
                      <td className="text-right px-3 py-2">{formatCompact(round.valuation)}</td>
                      <td className="text-right px-3 py-2">${round.pricePerShare.toFixed(2)}</td>
                      <td className="text-right px-3 py-2">
                        {isPast || profitBRL <= 0 ? (
                          <span className="text-muted-foreground">N/A</span>
                        ) : (
                          <span className="font-semibold text-emerald-600">
                            {formatCurrency(profitBRL)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasEquity && (
        <div className="rounded-lg bg-muted/50 px-4 py-3 text-xs text-muted-foreground">
          <strong>Vesting:</strong> 4 anos (1 ano cliff + 3 anos mensal) &middot;{" "}
          <strong>Strike:</strong> ${strikePrice.toFixed(2)} &middot;{" "}
          <strong>Diluição est.:</strong> ~15%/rodada
        </div>
      )}
    </div>
  );
}

function CompCard({
  label,
  value,
  sublabel,
  highlight,
  positive,
}: {
  label: string;
  value: string;
  sublabel?: string;
  highlight?: boolean;
  positive?: boolean;
}) {
  return (
    <div className="rounded-lg border bg-background p-4 text-center">
      <div className="text-[10px] font-medium uppercase text-muted-foreground">{label}</div>
      <div
        className={`mt-1 text-lg font-bold ${highlight ? "text-pink-600" : positive ? "text-emerald-600" : ""}`}
      >
        {value}
      </div>
      {sublabel && <div className="text-[10px] text-muted-foreground">{sublabel}</div>}
    </div>
  );
}
